import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Field, TextInput, TextArea, Toggle, ImageInput, SaveBar } from "@/admin/ui";

const SUBTABS = ["Brand", "Contact", "Social", "Floating Buttons", "Navigation", "Footer", "WhatsApp Templates"];

function ArrayEditor({ items, onChange, fields, addLabel, testid }) {
  const list = items || [];
  const set = (i, key, v) => onChange(list.map((it, idx) => (idx === i ? { ...it, [key]: v } : it)));
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const add = () => {
    const blank = {};
    fields.forEach((f) => (blank[f.key] = ""));
    onChange([...list, blank]);
  };
  return (
    <div className="space-y-3">
      {list.map((item, i) => (
        <div key={i} className="glass rounded-xl p-4 grid sm:grid-cols-2 gap-x-4 items-start" data-testid={`${testid}-item-${i}`}>
          {fields.map((f) => (
            <Field key={f.key} label={f.label}>
              <TextInput value={item[f.key]} onChange={(e) => set(i, f.key, e.target.value)} testid={`${testid}-${i}-${f.key}`} />
            </Field>
          ))}
          <div className="sm:col-span-2 flex justify-end">
            <button onClick={() => remove(i)} className="text-[11px] font-semibold text-rose-400 hover:text-rose-300" data-testid={`${testid}-remove-${i}`}>Remove</button>
          </div>
        </div>
      ))}
      <button onClick={add} className="w-full rounded-xl border border-dashed border-white/15 text-mist hover:text-white hover:border-accent/50 py-3 text-xs font-semibold transition-colors" data-testid={`${testid}-add-btn`}>
        + {addLabel}
      </button>
    </div>
  );
}

export default function SettingsManager() {
  const { settings, reload } = useSite();
  const [draft, setDraft] = useState(null);
  const [sub, setSub] = useState("Brand");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings && !draft) setDraft(JSON.parse(JSON.stringify(settings)));
  }, [settings]);

  if (!draft) {
    return <div className="py-24 flex justify-center"><Loader2 className="h-7 w-7 text-accent-bright animate-spin" /></div>;
  }

  const setField = (section, key, value) =>
    setDraft((d) => ({ ...d, [section]: { ...d[section], [key]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        brand: draft.brand, contact: draft.contact, social: draft.social, floating: draft.floating,
        nav: draft.nav, footer: draft.footer, templates: draft.templates,
      });
      await reload();
      toast.success("Settings saved — live on site");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const linkLines = (links) => (links || []).map((l) => `${l.label} :: ${l.href}`).join("\n");
  const toLinks = (txt) => String(txt || "").split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
    const idx = l.indexOf("::");
    return idx === -1 ? { label: l, href: "/" } : { label: l.slice(0, idx).trim(), href: l.slice(idx + 2).trim() || "/" };
  });
  const setCol = (ci, key, value) => {
    setDraft((d) => {
      const cols = (d.footer?.columns || []).map((c, i) => (i === ci ? { ...c, [key]: value } : c));
      return { ...d, footer: { ...d.footer, columns: cols } };
    });
  };

  return (
    <div data-testid="admin-settings-page">
      <h1 className="font-display text-xl sm:text-2xl font-bold text-white tracking-tight mb-2">Settings Manager</h1>
      <p className="text-xs text-subtle mb-6">Brand, contact channels, navigation, footer and WhatsApp message templates.</p>

      <div className="flex gap-2 flex-wrap mb-8">
        {SUBTABS.map((t) => (
          <button
            key={t}
            onClick={() => setSub(t)}
            className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all duration-300 ${sub === t ? "bg-accent border-accent text-white" : "border-white/15 text-mist hover:text-white hover:border-accent/50"}`}
            data-testid={`settings-subtab-${t.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="glass rounded-2xl p-6 sm:p-8">
        {sub === "Brand" && (
          <div className="grid sm:grid-cols-2 gap-x-4">
            <Field label="Brand Name"><TextInput value={draft.brand?.name} onChange={(e) => setField("brand", "name", e.target.value)} testid="settings-brand-name" /></Field>
            <Field label="Short Name (navbar)"><TextInput value={draft.brand?.short_name} onChange={(e) => setField("brand", "short_name", e.target.value)} testid="settings-brand-short" /></Field>
            <Field label="Tagline"><TextInput value={draft.brand?.tagline} onChange={(e) => setField("brand", "tagline", e.target.value)} testid="settings-brand-tagline" /></Field>
            <div className="sm:col-span-2 grid sm:grid-cols-2 gap-x-4">
              <Field label="Logo (circle works best)"><ImageInput value={draft.brand?.logo_url} onChange={(v) => setField("brand", "logo_url", v)} testid="settings-brand-logo" /></Field>
              <Field label="Favicon URL"><ImageInput value={draft.brand?.favicon_url} onChange={(v) => setField("brand", "favicon_url", v)} testid="settings-brand-favicon" /></Field>
            </div>
          </div>
        )}

        {sub === "Contact" && (
          <div className="grid sm:grid-cols-2 gap-x-4">
            <Field label="WhatsApp Number" hint="With country code, digits only — e.g. 919044288987"><TextInput value={draft.contact?.whatsapp} onChange={(e) => setField("contact", "whatsapp", e.target.value)} testid="settings-contact-whatsapp" /></Field>
            <Field label="Phone (display)"><TextInput value={draft.contact?.phone} onChange={(e) => setField("contact", "phone", e.target.value)} testid="settings-contact-phone" /></Field>
            <Field label="Primary Email"><TextInput value={draft.contact?.email_primary} onChange={(e) => setField("contact", "email_primary", e.target.value)} testid="settings-contact-email1" /></Field>
            <Field label="Support Email"><TextInput value={draft.contact?.email_support} onChange={(e) => setField("contact", "email_support", e.target.value)} testid="settings-contact-email2" /></Field>
            <Field label="Address"><TextInput value={draft.contact?.address} onChange={(e) => setField("contact", "address", e.target.value)} testid="settings-contact-address" /></Field>
            <Field label="Working Hours"><TextInput value={draft.contact?.hours} onChange={(e) => setField("contact", "hours", e.target.value)} testid="settings-contact-hours" /></Field>
            <Field label="Google Maps Embed URL" hint="From Google Maps → Share → Embed iframe src"><TextInput value={draft.contact?.maps_embed} onChange={(e) => setField("contact", "maps_embed", e.target.value)} testid="settings-contact-maps" /></Field>
            <Field label="Google Business Profile Link"><TextInput value={draft.contact?.gbp_link} onChange={(e) => setField("contact", "gbp_link", e.target.value)} testid="settings-contact-gbp" /></Field>
          </div>
        )}

        {sub === "Social" && (
          <div className="grid sm:grid-cols-2 gap-x-4">
            {["facebook", "instagram", "linkedin", "youtube", "twitter", "messenger"].map((k) => (
              <Field key={k} label={k.charAt(0).toUpperCase() + k.slice(1)} hint="Leave empty to hide from footer">
                <TextInput value={draft.social?.[k]} onChange={(e) => setField("social", k, e.target.value)} testid={`settings-social-${k}`} />
              </Field>
            ))}
          </div>
        )}

        {sub === "Floating Buttons" && (
          <div className="space-y-4">
            {["whatsapp", "call", "instagram", "messenger"].map((k) => (
              <div key={k} className="glass rounded-xl p-4 sm:p-5" data-testid={`settings-floating-${k}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-white capitalize">{k}</span>
                  <Toggle checked={!!draft.floating?.[k]?.enabled} onChange={(v) => setField("floating", k, { ...(draft.floating?.[k] || {}), enabled: v })} testid={`settings-floating-toggle-${k}`} />
                </div>
                <Field label="Number / Link">
                  <TextInput value={draft.floating?.[k]?.value} onChange={(e) => setField("floating", k, { ...(draft.floating?.[k] || {}), value: e.target.value })} testid={`settings-floating-value-${k}`} />
                </Field>
              </div>
            ))}
          </div>
        )}

        {sub === "Navigation" && (
          <div>
            <p className="text-xs text-subtle mb-4">Custom navbar links — these render in the header and mobile drawer.</p>
            <ArrayEditor
              items={draft.nav}
              onChange={(v) => setDraft((d) => ({ ...d, nav: v }))}
              fields={[{ key: "label", label: "Label" }, { key: "href", label: "Link (e.g. /about)" }]}
              addLabel="Add Nav Link"
              testid="settings-nav-editor"
            />
          </div>
        )}

        {sub === "Footer" && (
          <div className="space-y-6">
            <Field label="About Blurb"><TextArea value={draft.footer?.about} onChange={(e) => setField("footer", "about", e.target.value)} rows={3} testid="settings-footer-about" /></Field>
            <Field label="Copyright Line" hint="Year is added automatically"><TextInput value={draft.footer?.copyright} onChange={(e) => setField("footer", "copyright", e.target.value)} testid="settings-footer-copyright" /></Field>
            {(draft.footer?.columns || []).map((col, ci) => (
              <div key={ci} className="glass rounded-xl p-4 sm:p-5" data-testid={`settings-footer-column-${ci}`}>
                <Field label={`Column ${ci + 1} Title`}>
                  <TextInput value={col.title} onChange={(e) => setCol(ci, "title", e.target.value)} testid={`settings-footer-col-title-${ci}`} />
                </Field>
                <Field label="Links (one per line as 'Label :: /href')">
                  <TextArea value={linkLines(col.links)} onChange={(e) => setCol(ci, "links", toLinks(e.target.value))} rows={5} mono testid={`settings-footer-col-links-${ci}`} />
                </Field>
              </div>
            ))}
          </div>
        )}

        {sub === "WhatsApp Templates" && (
          <div className="space-y-4">
            <Field label="General Enquiry Message" hint="Used by all generic WhatsApp buttons">
              <TextArea value={draft.templates?.general} onChange={(e) => setField("templates", "general", e.target.value)} rows={3} testid="settings-template-general" />
            </Field>
            <Field label="Design Selection Message" hint="{design} is replaced with the sample name">
              <TextArea value={draft.templates?.design_selection} onChange={(e) => setField("templates", "design_selection", e.target.value)} rows={3} testid="settings-template-design" />
            </Field>
            <Field label="UGC Model Selection Message" hint="{model} and {language} are replaced automatically">
              <TextArea value={draft.templates?.ugc_selection} onChange={(e) => setField("templates", "ugc_selection", e.target.value)} rows={3} testid="settings-template-ugc" />
            </Field>
          </div>
        )}

        <SaveBar onSave={save} saving={saving} testid="settings-save-btn" />
      </div>
    </div>
  );
}
